import * as React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { Wordmark } from '@/components/layout/Wordmark';
import { LocaleToggle } from '@/components/LocaleToggle';
import { Card, CardContent } from '@/components/ui/card';
import { FormError } from '@/components/common/FormError';
import { OAuthButtons, OrDivider } from '@/components/auth/OAuthButtons';
import { SendAgainButton } from '@/components/invite/SendAgainButton';
import { SignInLinkForm } from '@/components/invite/SignInLinkForm';
import { oauthProviders } from '@/lib/config';

/**
 * self-signup (design section 4): the public door of a new agency's 30-day trial. An emailed link or code
 * that CREATES the account (`createUser: true`), or Google / Apple. Password sign-up stays closed at Caddy
 * on purpose. The link and the OAuth return land on `/signin`, one of the two landing paths
 * (`isLinkLandingPath`), which goes on to the board: an account with no agency gets the no-agency screen
 * and its "Create my agency" form.
 */
export function SignUp() {
  const { t } = useTranslation();
  const { session, sendSignInLink, clearLinkError } = useAuth();
  const [sentTo, setSentTo] = React.useState<string | null>(null);
  const [oauthError, setOauthError] = React.useState<string | null>(null);
  const hasOAuth = Object.values(oauthProviders()).some(Boolean);

  if (session) return <Navigate to="/dashboard" replace />;

  return (
    <div className="grid min-h-full place-items-center bg-canvas p-6">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Wordmark tone="canvas" />
          <LocaleToggle />
        </div>

        <Card>
          <CardContent className="p-8">
            <h1 className="text-2xl font-semibold text-ink">{t('auth.signup.title')}</h1>
            <p className="mt-1 text-base text-ink-muted">{t('auth.signup.subtitle')}</p>

            {sentTo === null ? (
              <>
                {hasOAuth && (
                  <div className="mt-6 flex flex-col gap-4">
                    <FormError message={oauthError} onDismiss={() => setOauthError(null)} />
                    <OAuthButtons redirectPath="/signin" onError={(key) => setOauthError(t(`auth.errors.${key}`))} />
                    <OrDivider />
                  </div>
                )}
                <div className="mt-6 flex flex-col gap-4">
                  <p className="text-base text-ink-secondary">{t('auth.signup.body')}</p>
                  <SignInLinkForm
                    idPrefix="signup-link"
                    redirectPath="/signin"
                    createUser
                    onSent={(address) => {
                      clearLinkError();
                      setSentTo(address);
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="mt-6 flex flex-col gap-4">
                <p role="status" className="text-base text-ink">
                  {t('auth.signup.sent', { email: sentTo })}
                </p>
                <SendAgainButton onSend={async () => (await sendSignInLink(sentTo, '/signin', { createUser: true })).error} />
              </div>
            )}

            <p className="mt-6 text-base text-ink-secondary">
              {t('auth.signup.haveAccount')}{' '}
              <Link to="/signin" className="font-medium text-accent-base underline-offset-4 hover:underline">
                {t('auth.signup.signIn')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
